import { createSignal } from 'solid-js';
import { isOk } from '../api';
import { firstError } from '../api/envelope';
import { useToast } from '../components/MessageBox';
import { useTranslation } from './useTranslation';

export type DispatchState = 'pending' | 'ok' | 'error';
export interface DispatchResult { state: DispatchState; msg?: string }

interface FanOutMessages {
    success?: string;
    failure: string;
}

interface FanOutPacing {
    baseMs: number;
    jitterMs: number;
}

type BotCall = (botId: string) => Promise<{ data: unknown }>;

// Module level so the group bar can show the outcome of a run started by whichever page is open.
const [fanOutResults, setFanOutResults] = createSignal<Record<string, DispatchResult>>({});
const [fanOutRunning, setFanOutRunning] = createSignal(false);

export { fanOutResults, fanOutRunning };

export const clearFanOutResults = () => setFanOutResults({});

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export function useBotFanOut() {
    const toast = useToast();
    const { t } = useTranslation();

    const setResult = (botId: string, state: DispatchState, msg?: string) =>
        setFanOutResults(prev => ({ ...prev, [botId]: { state, msg } }));

    const callOne = async (botId: string, call: BotCall, failure: string): Promise<boolean> => {
        const res = await call(botId);
        const envelope = res.data as Parameters<typeof isOk>[0] & Parameters<typeof firstError>[0];
        if (isOk(envelope)) {
            setResult(botId, 'ok');
            return true;
        }
        const serverMessage = firstError(envelope);
        setResult(botId, 'error', serverMessage ? t(serverMessage) : failure);
        return false;
    };

    // One target keeps the page's own toast; several get one summary plus a result pill per bot.
    const run = async (botIds: readonly string[], call: BotCall, messages: FanOutMessages, pacing?: FanOutPacing): Promise<string[]> => {
        if (botIds.length === 0 || fanOutRunning()) return [];
        setFanOutRunning(true);
        setFanOutResults(Object.fromEntries(botIds.map(botId => [botId, { state: 'pending' as const }])));

        const failedIds: string[] = [];
        if (pacing && (pacing.baseMs > 0 || pacing.jitterMs > 0)) {
            for (let i = 0; i < botIds.length; i++) {
                if (!await callOne(botIds[i], call, messages.failure)) failedIds.push(botIds[i]);
                if (i < botIds.length - 1)
                    await wait(Math.max(0, pacing.baseMs + (Math.random() * 2 - 1) * pacing.jitterMs));
            }
        } else {
            await Promise.all(botIds.map(async botId => {
                if (!await callOne(botId, call, messages.failure)) failedIds.push(botId);
            }));
        }
        setFanOutRunning(false);

        if (botIds.length === 1) {
            if (failedIds.length === 0) { if (messages.success) toast?.info(messages.success); }
            else toast?.error(fanOutResults()[botIds[0]]?.msg ?? messages.failure);
        } else {
            const summary = t('MULTI_DISPATCH_SUMMARY', String(botIds.length), String(failedIds.length));
            if (failedIds.length === 0) toast?.info(summary);
            else toast?.error(summary);
        }
        return failedIds;
    };

    return { run, results: fanOutResults, running: fanOutRunning };
}
